/**
 * Done. / ChiroCPD — useReflections Hook
 *
 * Four-state machine for AI-drafted reflections:
 *   idle → generating (AbortController) → draftReady → (edit/save)
 *   idle → capReached (when AI cap exhausted)
 *
 * AbortController cleanup on unmount prevents state updates on unmounted component.
 * Cap sidecar is auto-handled by api.ts + CapStatusContext.
 */

import { useState, useCallback, useRef } from 'react';
import { api, ApiError } from '@/lib/api';
import { cacheInvalidate } from '@/lib/cache';
import type { Reflection, ReflectionGenerationState } from '@/lib/types';

interface ReflectionInput {
  outcomeText: string;
  reflectionText: string;
}

interface ReflectionsHookResult {
  reflection: Reflection | null;
  state: ReflectionGenerationState;
  errorMessage: string | null;
  generate: (activityId: string) => Promise<void>;
  abort: () => void;
  save: (activityId: string, input: ReflectionInput, aiGenerated: boolean) => Promise<void>;
  setReflection: (reflection: Reflection | null) => void;
}

export function useReflections(initialReflection?: Reflection | null): ReflectionsHookResult {
  const [reflection, setReflection] = useState(initialReflection ?? null);
  const [state, setState] = useState<ReflectionGenerationState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState('idle');
    setErrorMessage(null);
  }, []);

  const generate = useCallback(async (activityId: string): Promise<void> => {
    setState('generating');
    setErrorMessage(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const generated = await api.post<Reflection>(
        '/api/ai/generate-reflection',
        { activityId },
        controller.signal,
      );
      // api.ts extracts capStatus sidecar → CapStatusContext auto-updates
      setReflection(generated);
      setState('draftReady');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setState('capReached');
        return;
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setState('idle');
        return;
      }
      setErrorMessage(err instanceof Error ? err.message : 'Failed to generate reflection');
      setState('error');
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  const save = useCallback(async (
    activityId: string,
    input: ReflectionInput,
    aiGenerated: boolean,
  ): Promise<void> => {
    const saved = await api.post<Reflection>('/api/reflections', {
      activityId,
      outcomeText: input.outcomeText,
      reflectionText: input.reflectionText,
      aiGenerated,
    });
    setReflection(saved);
    setState('idle');
    // Invalidate export cache — reflection populates fields 7 & 8
    cacheInvalidate('export-fields');
  }, []);

  return {
    reflection,
    state,
    errorMessage,
    generate,
    abort,
    save,
    setReflection,
  };
}
